import React, { FC, ReactElement, useRef } from 'react';
import clsx from 'clsx';
import styles from './Button.style';
import { Spinner } from 'components/common/Spinner/Spinner';
import * as colors from 'foundation/colors';
import { ButtonTypes } from 'types/enums';

export enum Size {
    SMALL = 'small',
    MEDIUM = 'medium',
}

interface ButtonProps extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'style'
> {
    type: ButtonTypes;
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    style?: string;
    compact?: boolean;
    iconLeft?: ReactElement;
    iconRight?: ReactElement;
    htmlType?: 'button' | 'submit' | 'reset' | undefined;
    focusOnTab?: boolean;
    size?: Size;
    dataTestId?: string;
    onClick?: () => void;
}

export const Button: FC<ButtonProps> = ({
    loading,
    disabled,
    type,
    label,
    style,
    compact,
    iconLeft,
    iconRight,
    htmlType = 'button',
    focusOnTab = true,
    size = Size.MEDIUM,
    dataTestId,
    onClick,
    ...rest
}) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const classes = styles();

    return (
        <button
            ref={buttonRef}
            className={clsx(
                classes.button,

                // variants
                type === ButtonTypes.PRIMARY && classes.primaryButton,
                type === ButtonTypes.SECONDARY && classes.secondaryButton,
                type === ButtonTypes.SUBTLE && classes.subtleButton,
                type === ButtonTypes.DESTRUCTIVE && classes.destructiveButton,
                type === ButtonTypes.LINK && classes.linkButton,
                type === ButtonTypes.ICON && classes.iconButton,
                type === ButtonTypes.GO_BACK && classes.backButton,

                // modifiers
                compact && classes.compact,
                (iconLeft || iconRight) && classes.withIcon,
                size === Size.SMALL && classes.small,

                loading && classes.loading,
                style,
            )}
            disabled={disabled || loading}
            {...(dataTestId && { 'data-testid': dataTestId })}
            type={htmlType}
            {...(!focusOnTab && { tabIndex: -1 })}
            {...rest}
            onClick={() => {
                if (!loading && !disabled && onClick) {
                    onClick();
                }
            }}
        >
            <div className={classes.buttonContent}>
                {loading ? (
                    <Spinner
                        fill={
                            type === ButtonTypes.PRIMARY
                                ? colors.neutral.white
                                : colors.prussianBlue
                        }
                    />
                ) : (
                    <>
                        {iconLeft &&
                            // Rendering the icon when it's a ReactComponent prop
                            (React.isValidElement(iconLeft) ? (
                                iconLeft
                            ) : (
                                // Rendering the icon when it's a svg file uploaded from the Storybook UI
                                <img src={iconLeft} alt="" />
                            ))}
                        {label}
                        {iconRight &&
                            (React.isValidElement(iconRight) ? (
                                iconRight
                            ) : (
                                <img src={iconRight} alt="" />
                            ))}
                    </>
                )}
            </div>
        </button>
    );
};
